import React, { Fragment, useState } from 'react'
import { Box, Divider, Grid, List, Tab, Tabs, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Skeleton } from '@material-ui/lab'
import { compareAsc, isAfter, parse } from 'date-fns'
import { isNil } from 'lodash'

import CardWrapper from '../admin/CardWrapper'
import CardActionButton from '../admin/CardActionButton'
import { formatProgramOverview } from '../admin/adminUtils'
import TitleCard from '../common/TitleCard'
import TabPanel from '../../common/TabPanel'
import DialogCreateProgram from './DialogCreateProgram'
import DialogProgramDetails from './DialogProgramDetails'
import ListItemPerk from './ListItemPerk'
import ChipProgramStatus from './ChipProgramStatus'

const useStyles = makeStyles({
  emptyState: {
    fontWeight: 'normal',
    fontStyle: 'italic',
  },
  hiddenTabs: {
    visibility: 'hidden',
  },
  visibleTabs: {
    visibility: 'visible',
  },
})

export default function CardPrograms({
  team,
  programs,
  defaultPaymentMethod,
  loading,
  selectedProgram,
  setSelectedProgram,
  resetProgramDetailsState,
}) {
  const classes = useStyles()
  const today = new Date()
  const [tabValue, setTabValue] = useState(0)
  const [showProgramDialog, setShowProgramDialog] = useState(false)

  // programs that have no end date or whose end date has not passed
  const activePrograms =
    programs?.filter(program => program.endDate === null || isAfter(program.endDate, today)) ?? []

  // programs whose end date has passed
  const inactivePrograms =
    programs?.filter(
      program => program.endDate !== null && compareAsc(program.endDate, today) <= 0,
    ) ?? []

  const handleChange = (event, value) => {
    setTabValue(value)
  }

  const handleCloseProgramDetails = () => {
    resetProgramDetailsState()
  }

  if (loading) {
    return <LoadingState />
  }

  return (
    <>
      <CardWrapper>
        <Grid container>
          <Grid item sm={4} xs={12}>
            <TitleCard>Employee Subsidies</TitleCard>
          </Grid>
          <Grid
            item
            sm={6}
            xs={6}
            style={{ maxHeight: '40px', position: 'relative', bottom: '8px' }}
          >
            <Tabs
              value={tabValue}
              onChange={handleChange}
              indicatorColor="primary"
              textColor="primary"
              aria-label="active/inactive subsidies"
              className={inactivePrograms.length > 0 ? classes.visibleTabs : classes.hiddenTabs}
            >
              <Tab label="ACTIVE" data-testid="tab-active" />
              <Tab label="INACTIVE" data-testid="tab-inactive" />
            </Tabs>
          </Grid>
          <Grid item sm={2} xs={6}>
            <CardActionButton name="+ Add Subsidy" onClick={() => setShowProgramDialog(true)} />
          </Grid>
        </Grid>
        <Box paddingTop={2}>
          {programs?.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              <TabPanel value={tabValue} index={0}>
                <ActivePrograms classes={classes} today={today} onClick={setSelectedProgram}>
                  {activePrograms}
                </ActivePrograms>
              </TabPanel>
              <TabPanel value={tabValue} index={1}>
                <InactivePrograms classes={classes} onClick={setSelectedProgram}>
                  {inactivePrograms}
                </InactivePrograms>
              </TabPanel>
            </>
          )}
        </Box>
      </CardWrapper>
      {showProgramDialog && (
        <DialogCreateProgram
          team={team}
          defaultPaymentMethod={defaultPaymentMethod}
          onClose={() => setShowProgramDialog(false)}
        />
      )}
      {!isNil(selectedProgram) && (
        <DialogProgramDetails program={selectedProgram} onClose={handleCloseProgramDetails} />
      )}
    </>
  )
}

const LoadingState = () => {
  return (
    <CardWrapper>
      <Box padding={3}>
        <Grid container>
          <Grid item xs={10}>
            <TitleCard>Employee Subsidies</TitleCard>
          </Grid>
        </Grid>
        <Skeleton height={100} />
      </Box>
    </CardWrapper>
  )
}

const EmptyState = () => {
  const classes = useStyles()
  return (
    <Box padding={3}>
      <Typography variant="h6" className={classes.emptyState}>
        No Current Subsidies
      </Typography>
      <Typography variant="body1">
        Pay for lunch for your team members with a subsidy program.
      </Typography>
    </Box>
  )
}

const ActivePrograms = ({ children, classes, today, onClick }) => {
  return (
    <>
      {children.length > 0 ? (
        <List>
          {children.map((program, index) => (
            <Fragment key={program.programId}>
              <ListItemPerk
                primaryText={program.name}
                secondaryText={formatProgramOverview(program)}
                onClick={() => onClick(program)}
                ActionComponent={
                  parse(program.startDate) <= today ? (
                    <ChipProgramStatus status="active" />
                  ) : (
                    <ChipProgramStatus status="upcoming" />
                  )
                }
              />
              {index < children.length - 1 && <Divider />}
            </Fragment>
          ))}
        </List>
      ) : (
        <EmptyState className={classes.emptyState} />
      )}
    </>
  )
}

const InactivePrograms = ({ children, classes, onClick }) => {
  return (
    <List>
      {children.map((program, index) => (
        <Fragment key={program.programId}>
          <ListItemPerk
            primaryText={program.name}
            secondaryText={formatProgramOverview(program)}
            onClick={() => onClick(program)}
            ActionComponent={<ChipProgramStatus status="inactive" />}
          />
          {index < children.length - 1 && (
            <Box paddingTop={2} paddingBottom={2}>
              <Divider />
            </Box>
          )}
        </Fragment>
      ))}
    </List>
  )
}
